import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { styled } from '@mui/material/styles';
import { Search, Mail, Phone, ExternalLink, AlertTriangle, CheckCircle2, XCircle, HelpCircle, Activity as MonitorLogoIcon, FileText, ArrowDownUp } from 'lucide-react';
import api from '../services/api';
import { useJob } from '../context/JobContext';
import { useAuth } from '../context/AuthContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LoaderProgress from '../components/ui/loader-progress';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(2.5),
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.08)',
}));

export default function PraticheMonitoraggio() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addMonitoringNotification, addJob } = useJob();
  const { user: authUser, loading: authLoading } = useAuth();
  const roleName = (authUser?.role_name || authUser?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';
  const initDoneRef = useRef(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [summary, setSummary] = useState({
    in_regola: 0,
    attenzione: 0,
    critico: 0,
    senza_note: 0,
  });
  const [filteredList, setFilteredList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBucket, setSelectedBucket] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [issuerOptions, setIssuerOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [urgencyOptions, setUrgencyOptions] = useState([]);
  const [selectedIssuer, setSelectedIssuer] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedUrgency, setSelectedUrgency] = useState('');
  const [referenceDate, setReferenceDate] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [introOpen, setIntroOpen] = useState(true);
  const [comparePeriod, setComparePeriod] = useState('');
  const [previousSummary, setPreviousSummary] = useState(null);
  const [previousDateDisplay, setPreviousDateDisplay] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [legalDistribution, setLegalDistribution] = useState([]);
  const [viewTab, setViewTab] = useState('list');
  const [onlyLegalNotes, setOnlyLegalNotes] = useState(false);
  const [incassatoPercentRange, setIncassatoPercentRange] = useState('');
  const [decadenzaFrom, setDecadenzaFrom] = useState('');
  const [decadenzaTo, setDecadenzaTo] = useState('');
  const [confirmReportOpen, setConfirmReportOpen] = useState(false);
  const [confirmDefaultSortOpen, setConfirmDefaultSortOpen] = useState(false);
  const [orderBy, setOrderBy] = useState('giorni_da_ultima_nota');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    if (initDoneRef.current) return;
    if (authLoading) return;
    if (!authUser) return;

    const params = new URLSearchParams(location.search);
    const rif = params.get('rif_responsabile_legale');
    const statusQ = params.get('status');
    const refDateQ = params.get('reference_date') || params.get('al');
    const bucketQ = params.get('bucket');
    const searchQ = params.get('search');
    const issuerQ = params.get('issuer');
    const categoryQ = params.get('category');
    const urgencyQ = params.get('urgency');
    const onlyLegalNotesQ = params.get('only_legal_notes');
    const incassatoPercentRangeQ = params.get('incassato_percent_range');
    const decadenzaFromQ = params.get('decadenza_from');
    const decadenzaToQ = params.get('decadenza_to');
    const incassatoPercentRangeInitial =
      typeof incassatoPercentRangeQ === 'string' ? incassatoPercentRangeQ : '';
    const decadenzaFromInitial =
      typeof decadenzaFromQ === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(decadenzaFromQ)
        ? decadenzaFromQ
        : '';
    const decadenzaToInitial =
      typeof decadenzaToQ === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(decadenzaToQ)
        ? decadenzaToQ
        : '';

    const currentUserOption = {
      id: authUser?.id,
      name: authUser?.name || '',
      surname: authUser?.surname || '',
    };
    const initialUser = isAdmin ? null : currentUserOption;

    let hasRif = false;
    if (isAdmin && rif) {
      const idNum = parseInt(rif, 10);
      if (!Number.isNaN(idNum) && (idNum > 0 || idNum === -1)) {
        hasRif = true;
        setPendingUserId(idNum);
      }
    }
    const statusInitial = typeof statusQ === 'string' ? statusQ : '';
    const refDateInitial =
      typeof refDateQ === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(refDateQ)
        ? refDateQ
        : referenceDate;
    const bucketInitial = typeof bucketQ === 'string' ? bucketQ : '';
    const searchInitial = typeof searchQ === 'string' ? searchQ : '';
    const issuerInitial = typeof issuerQ === 'string' ? issuerQ : '';
    const categoryInitial = typeof categoryQ === 'string' ? categoryQ : '';
    const urgencyInitial = typeof urgencyQ === 'string' ? urgencyQ : '';
    const onlyLegalNotesInitial = onlyLegalNotesQ === 'true';

    if (!isAdmin) {
      setUsers([currentUserOption]);
      setSelectedUser(currentUserOption);
      setPendingUserId(null);
    }
    setSelectedStatus(statusInitial);
    setReferenceDate(refDateInitial);
    setSelectedBucket(bucketInitial);
    setSearchTerm(searchInitial);
    setSelectedIssuer(issuerInitial);
    setSelectedCategory(categoryInitial);
    setSelectedUrgency(urgencyInitial);
    setOnlyLegalNotes(onlyLegalNotesInitial);
    setIncassatoPercentRange(incassatoPercentRangeInitial);
    setDecadenzaFrom(decadenzaFromInitial);
    setDecadenzaTo(decadenzaToInitial);

    setPage(0);
    if (!hasRif) {
      fetchMonitoring(initialUser, {
        status: statusInitial,
        referenceDate: refDateInitial,
        bucket: bucketInitial,
        search: searchInitial,
        issuer: issuerInitial,
        category: categoryInitial,
        urgency: urgencyInitial,
        onlyLegalNotes: onlyLegalNotesInitial,
        incassatoPercentRange: incassatoPercentRangeInitial,
        decadenzaFrom: decadenzaFromInitial,
        decadenzaTo: decadenzaToInitial,
        page: 0,
      });
    }

    initDoneRef.current = true;
  }, [authLoading, authUser, isAdmin, location.search, referenceDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authLoading) return;
    if (!authUser) return;
    if (!isAdmin) return;

    const fetchUsers = async () => {
      try {
        const res = await api.get('/lorenzo-users');
        const data = res.data || [];
        const sorted = [...data].sort((a, b) => {
          const nameA = [a.name, a.surname].filter(Boolean).join(' ') || '';
          const nameB = [b.name, b.surname].filter(Boolean).join(' ') || '';
          return nameA.localeCompare(nameB, 'it', { sensitivity: 'base' });
        });
        const specialOption = {
          id: -1,
          name: 'Senza referente legale',
          surname: '',
        };
        setUsers([specialOption, ...sorted]);
      } catch (error) {
        console.error('Error fetching users for monitoraggio pratiche:', error);
      }
    };
    fetchUsers();
  }, [authLoading, authUser, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    if (pendingUserId == null) return;
    if (!users || users.length === 0) return;
    const found = users.find((u) => u && u.id === pendingUserId);
    setPendingUserId(null);
    if (!found) return;
    setSelectedUser(found);
    setPage(0);
    fetchMonitoring(found, { page: 0 });
  }, [isAdmin, pendingUserId, users]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [catRes, issuerRes, filtersRes] = await Promise.all([
          api.get('/category-negotiation'),
          api.get('/issuer'),
          api.get('/negotiation/filters'),
        ]);

        setCategoryOptions(
          [...new Set(
            (catRes.data || [])
              .map((item) => item.name)
              .filter(Boolean)
          )].sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }))
        );

        setIssuerOptions(
          [...new Set(
            (issuerRes.data || [])
              .map((item) => item.name)
              .filter(Boolean)
          )].sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }))
        );

        const data = filtersRes.data || {};
        setStatusOptions((data.statuses || []).filter(Boolean));
        setUrgencyOptions((data.urgencies || []).filter(Boolean));
      } catch (error) {
        console.error('Error fetching negotiation filter options for monitoraggio:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  const handleBucketClick = (bucket) => {
    const next = selectedBucket === bucket ? '' : bucket;
    setSelectedBucket(next);
    setPage(0);
    fetchMonitoring(selectedUser || null, { bucket: next, page: 0 });
  };

  const fetchMonitoring = async (user, options = {}) => {
    setLoading(true);
    try {
      const hasStatus = Object.prototype.hasOwnProperty.call(options, 'status');
      const hasRefDate = Object.prototype.hasOwnProperty.call(options, 'referenceDate');
      const hasCompare = Object.prototype.hasOwnProperty.call(options, 'comparePeriod');
      const hasPage = Object.prototype.hasOwnProperty.call(options, 'page');
      const hasRows = Object.prototype.hasOwnProperty.call(options, 'rowsPerPage');
      const hasSearch = Object.prototype.hasOwnProperty.call(options, 'search');
      const hasBucket = Object.prototype.hasOwnProperty.call(options, 'bucket');
      const hasOnlyLegalNotes = Object.prototype.hasOwnProperty.call(
        options,
        'onlyLegalNotes'
      );
      const hasIncassatoPercentRange = Object.prototype.hasOwnProperty.call(
        options,
        'incassatoPercentRange'
      );
      const hasDecadenzaFrom = Object.prototype.hasOwnProperty.call(options, 'decadenzaFrom');
      const hasDecadenzaTo = Object.prototype.hasOwnProperty.call(options, 'decadenzaTo');
      const currentIncassatoPercentRange = hasIncassatoPercentRange
        ? options.incassatoPercentRange
        : incassatoPercentRange;
      const currentDecadenzaFrom = hasDecadenzaFrom ? options.decadenzaFrom : decadenzaFrom;
      const currentDecadenzaTo = hasDecadenzaTo ? options.decadenzaTo : decadenzaTo;
      const hasOrderBy = Object.prototype.hasOwnProperty.call(options, 'orderBy');
      const hasOrder = Object.prototype.hasOwnProperty.call(options, 'order');
      const status = hasStatus ? options.status : selectedStatus || '';
      const refDate = hasRefDate ? options.referenceDate : referenceDate || '';
      const effectiveCompare = hasCompare ? options.comparePeriod : comparePeriod;
      const currentPage = hasPage ? options.page : page;
      const currentRowsPerPage = hasRows ? options.rowsPerPage : rowsPerPage;
      const currentSearch = hasSearch ? options.search : searchTerm;
      const currentBucket = hasBucket ? options.bucket : selectedBucket;
      const currentIssuer = Object.prototype.hasOwnProperty.call(options, 'issuer')
        ? options.issuer
        : selectedIssuer;
      const currentCategory = Object.prototype.hasOwnProperty.call(options, 'category')
        ? options.category
        : selectedCategory;
      const currentUrgency = Object.prototype.hasOwnProperty.call(options, 'urgency')
        ? options.urgency
        : selectedUrgency;
      const onlyLegal = hasOnlyLegalNotes ? options.onlyLegalNotes : onlyLegalNotes;
      const currentOrderBy = hasOrderBy ? options.orderBy : orderBy;
      const currentOrder = hasOrder ? options.order : order;
      const params = {
        status,
        reference_date: refDate,
        page: currentPage,
        limit: currentRowsPerPage,
        orderBy: currentOrderBy,
        order: currentOrder,
        include_financials: 'true',
      };
      if (user && user.id) {
        params.rif_responsabile_legale = user.id;
      }
      if (currentSearch) {
        params.search = currentSearch;
      }
      if (currentBucket) {
        params.bucket = currentBucket;
      }
      if (currentIssuer) {
        params.issuer = currentIssuer;
      }
      if (currentCategory) {
        params.category = currentCategory;
      }
      if (currentUrgency) {
        params.urgency = currentUrgency;
      }
      if (onlyLegal) {
        params.only_legal_notes = 'true';
      }
      if (currentDecadenzaFrom) {
        params.decadenza_from = currentDecadenzaFrom;
      }
      if (currentDecadenzaTo) {
        params.decadenza_to = currentDecadenzaTo;
      }
      if (currentIncassatoPercentRange) {
        params.incassato_percent_range = currentIncassatoPercentRange;
      }
      const res = await api.get('/negotiation/monitoring', { params });
      const fetchedList = res.data.list || [];
      setSummary(res.data.summary || { in_regola: 0, attenzione: 0, critico: 0, senza_note: 0 });
      setFilteredList(fetchedList);

      try {
        const distParams = {
          status,
          reference_date: refDate,
        };
        if (currentIssuer) {
          distParams.issuer = currentIssuer;
        }
        if (currentCategory) {
          distParams.category = currentCategory;
        }
        if (currentUrgency) {
          distParams.urgency = currentUrgency;
        }
        if (onlyLegal) {
          distParams.only_legal_notes = 'true';
        }
        if (currentDecadenzaFrom) {
          distParams.decadenza_from = currentDecadenzaFrom;
        }
        if (currentDecadenzaTo) {
          distParams.decadenza_to = currentDecadenzaTo;
        }
        if (currentIncassatoPercentRange) {
          distParams.incassato_percent_range = currentIncassatoPercentRange;
        }
        const distRes = await api.get('/negotiation/monitoring/legal-distribution', {
          params: distParams,
        });
        setLegalDistribution(distRes.data && distRes.data.distribution ? distRes.data.distribution : []);
      } catch (err) {
        console.error('Error fetching monitoring legal distribution:', err);
        setLegalDistribution([]);
      }

      if (res.data && res.data.meta) {
        addMonitoringNotification(res.data.meta);
        setTotalCount(res.data.meta.totalCount || 0);
      } else {
        setTotalCount((res.data.list || []).length);
      }

      if (effectiveCompare) {
        const prevDate = (() => {
          const d = new Date(refDate);
          if (Number.isNaN(d.getTime())) return null;
          if (effectiveCompare === 'ieri') {
            d.setDate(d.getDate() - 1);
          } else if (effectiveCompare === 'settimana') {
            d.setDate(d.getDate() - 7);
          } else if (effectiveCompare === 'mese') {
            d.setMonth(d.getMonth() - 1);
          } else {
            return null;
          }
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${dd}`;
        })();
        if (prevDate) {
          const parts = prevDate.split('-');
          const display = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : '';
          setPreviousDateDisplay(display);
          const prevParams = { ...params, reference_date: prevDate };
          const prevRes = await api.get('/negotiation/monitoring', { params: prevParams });
          setPreviousSummary(prevRes.data.summary || null);
        } else {
          setPreviousDateDisplay('');
          setPreviousSummary(null);
        }
      } else {
        setPreviousDateDisplay('');
        setPreviousSummary(null);
      }
    } catch (error) {
      console.error('Error fetching negotiation monitoring:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (event, value) => {
    setSelectedUser(value || null);
    fetchMonitoring(value || null);
  };

  const handleChangeStatus = (event) => {
    const value = event.target.value;
    setSelectedStatus(value);
    fetchMonitoring(selectedUser || null, { status: value });
  };

  const handleChangeIssuer = (event) => {
    const value = event.target.value;
    setSelectedIssuer(value);
    setPage(0);
    fetchMonitoring(selectedUser || null, { issuer: value, page: 0 });
  };

  const handleChangeCategory = (event) => {
    const value = event.target.value;
    setSelectedCategory(value);
    setPage(0);
    fetchMonitoring(selectedUser || null, { category: value, page: 0 });
  };

  const handleChangeUrgency = (event) => {
    const value = event.target.value;
    setSelectedUrgency(value);
    setPage(0);
    fetchMonitoring(selectedUser || null, { urgency: value, page: 0 });
  };

  const handleChangeReferenceDate = (event) => {
    const value = event.target.value;
    setReferenceDate(value);
    fetchMonitoring(selectedUser || null, { referenceDate: value });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchMonitoring(selectedUser || null, { page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    fetchMonitoring(selectedUser || null, { page: 0, rowsPerPage: newRows });
  };

  const formatDays = (days) => {
    if (days == null) return '-';
    const value = Number(days);
    if (Number.isNaN(value)) return days;

    let dotColor = 'success.main';
    if (value >= 31 && value <= 60) {
      dotColor = 'warning.main';
    } else if (value > 60) {
      dotColor = 'error.main';
    }

    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: dotColor,
          }}
        />
        <Typography component="span" sx={{ fontSize: '0.8rem' }}>
          {value}
        </Typography>
      </Box>
    );
  };

  const handleResetSort = () => {
    setOrderBy('giorni_da_ultima_nota');
    setOrder('desc');
    setPage(0);
    fetchMonitoring(selectedUser || null, {
      orderBy: 'giorni_da_ultima_nota',
      order: 'desc',
      page: 0
    });
  };

  const isDefaultSort =
    orderBy === 'giorni_da_ultima_nota' && String(order).toLowerCase() === 'desc';

  const visibleRows = filteredList;

  // Keep URL in sync with current filters so they persist on navigation/back
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedUser && selectedUser.id) {
      params.set('rif_responsabile_legale', String(selectedUser.id));
    }
    if (selectedStatus !== '') {
      params.set('status', selectedStatus);
    }
    if (referenceDate) {
      params.set('reference_date', referenceDate);
    }
    if (selectedBucket) {
      params.set('bucket', selectedBucket);
    }
    if (searchTerm) {
      params.set('search', searchTerm);
    }
    if (selectedIssuer) {
      params.set('issuer', selectedIssuer);
    }
    if (selectedCategory) {
      params.set('category', selectedCategory);
    }
    if (selectedUrgency) {
      params.set('urgency', selectedUrgency);
    }
    if (decadenzaFrom) {
      params.set('decadenza_from', decadenzaFrom);
    }
    if (decadenzaTo) {
      params.set('decadenza_to', decadenzaTo);
    }
    if (incassatoPercentRange) {
      params.set('incassato_percent_range', incassatoPercentRange);
    }
    if (onlyLegalNotes) {
      params.set('only_legal_notes', 'true');
    }
    const search = params.toString();
    navigate({ search }, { replace: true });
  }, [
    selectedUser,
    selectedStatus,
    referenceDate,
    selectedBucket,
    searchTerm,
    selectedIssuer,
    selectedCategory,
    selectedUrgency,
    incassatoPercentRange,
    decadenzaFrom,
    decadenzaTo,
    onlyLegalNotes,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartMonitoringReportJob = async () => {
    try {
      const effectiveActiveTab =
        viewTab === 'list' ? (selectedBucket || '') : viewTab;
      const selectedLegalName = selectedUser
        ? [selectedUser.name, selectedUser.surname].filter(Boolean).join(' ').trim() ||
          (selectedUser.id === -1 ? 'Senza referente legale' : '')
        : '';

      const payload = {
        filters: {
          status: selectedStatus || '',
          reference_date: referenceDate || '',
          search: searchTerm || '',
          bucket: selectedBucket || '',
          issuer: selectedIssuer || '',
          category: selectedCategory || '',
          urgency: selectedUrgency || '',
          decadenza_from: decadenzaFrom || '',
          decadenza_to: decadenzaTo || '',
          only_legal_notes: onlyLegalNotes,
          rif_responsabile_legale:
            selectedUser && typeof selectedUser.id === 'number' ? selectedUser.id : ''
        },
        active_tab: effectiveActiveTab,
        selected_legal_name: selectedLegalName
      };

      const res = await api.post('/reports/pratiche-monitoring', payload);
      if (res.data && res.data.jobId) {
        addJob(res.data.jobId, 'pratiche_monitoraggio');
      }
    } catch (error) {
      console.error('Error starting monitoraggio pratiche PDF report:', error);
    }
  };

  const formatDateForPreview = (isoDate) => {
    if (!isoDate || typeof isoDate !== 'string') return '-';
    const parts = isoDate.split('-');
    if (parts.length !== 3) return isoDate;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const formatCurrency = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(num);
  };

  const formatPercent = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    const clamped = Math.max(0, Math.min(100, num));
    return `${Math.round(clamped)}%`;
  };

  const renderPercentRing = (value) => {
    const num = Number(value);
    const clamped = Number.isFinite(num) ? Math.max(0, Math.min(100, num)) : 0;
    const label = formatPercent(clamped);
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={42}
          thickness={5}
          sx={{ color: 'grey.200' }}
        />
        <CircularProgress
          variant="determinate"
          value={clamped}
          size={42}
          thickness={5}
          sx={{
            position: 'absolute',
            left: 0,
            color: clamped >= 100 ? 'success.main' : 'warning.main',
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography component="span" sx={{ fontSize: 12, fontWeight: 800 }}>
            {label}
          </Typography>
        </Box>
      </Box>
    );
  };

  const handleSortColumn = (key) => {
    const nextOrder = orderBy === key ? (order === 'asc' ? 'desc' : 'asc') : 'desc';
    setOrder(nextOrder);
    setOrderBy(key);
    setPage(0);
    fetchMonitoring(selectedUser || null, { orderBy: key, order: nextOrder, page: 0 });
  };

  const activeTabForReport = viewTab === 'list' ? (selectedBucket || '') : viewTab;
  const activeTabLabelMap = {
    list: 'Lista',
    in_regola: 'In Regola (0-30 giorni)',
    attenzione: 'Attenzione (31-60 giorni)',
    critico: 'Critico (> 60 giorni)',
    senza_note: 'Senza note'
  };
  const reportFiltersPreview = [
    { key: 'Tab attivo', value: activeTabLabelMap[activeTabForReport] || 'Lista' },
    { key: 'Stato pratica', value: selectedStatus || 'Tutti' },
    {
      key: 'Referente legale',
      value: selectedUser
        ? [selectedUser.name, selectedUser.surname].filter(Boolean).join(' ').trim() ||
          (selectedUser.id === -1 ? 'Senza referente legale' : `ID ${selectedUser.id}`)
        : 'Tutti'
    },
    { key: 'Data riferimento', value: formatDateForPreview(referenceDate) },
    {
      key: 'Decadenza',
      value:
        decadenzaFrom || decadenzaTo
          ? `${formatDateForPreview(decadenzaFrom)} → ${formatDateForPreview(decadenzaTo)}`
          : 'Tutte'
    },
    { key: 'Controparte', value: selectedIssuer || 'Tutte' },
    { key: 'Tipologia', value: selectedCategory || 'Tutte' },
    { key: 'Urgenza', value: selectedUrgency || 'Tutte' },
    { key: 'Ricerca testuale', value: searchTerm || '-' },
    { key: 'Solo note referente legale', value: onlyLegalNotes ? 'Sì' : 'No' }
  ];

  const chartBucketKey =
    viewTab === 'in_regola'
      ? 'in_regola'
      : viewTab === 'attenzione'
      ? 'attenzione'
      : viewTab === 'critico'
      ? 'critico'
      : 'senza_note';

  const legalDistributionChartData = [...legalDistribution]
    .map((row) => ({
      name: row.name || (row.legal_id ? `ID: ${row.legal_id}` : 'Senza referente'),
      value: row[chartBucketKey] || 0
    }))
    .filter((item) => (item.value || 0) > 0)
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  const chartHeightPx = Math.min(
    Math.max(280, legalDistributionChartData.length * 30 + 80),
    900
  );
  const yAxisWidth = Math.min(
    Math.max(
      160,
      legalDistributionChartData.reduce(
        (max, row) => Math.max(max, String(row.name || '').length),
        0
      ) * 7
    ),
    280
  );
  const chartLeftMargin = Math.max(120, yAxisWidth + 24);

  return (
    <Box sx={{ p: 3 }}>
      <Dialog open={introOpen} onClose={() => setIntroOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Monitoraggio Pratiche</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            La procedura verifica, per tutte le pratiche in gestione, note e task associati,
            classificando ciascuna pratica nelle seguenti categorie:
          </Typography>
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <HelpCircle size={18} />
              <Typography> Senza note/task</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
              <CheckCircle2 size={18} />
              <Typography> Regolari: note/task più recenti di 30 giorni</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
              <AlertTriangle size={18} />
              <Typography> Attenzione: note/task tra 31 e 60 giorni</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
              <XCircle size={18} />
              <Typography> Critiche: note/task oltre 61 giorni</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIntroOpen(false)}>
            Ho capito
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmReportOpen}
        onClose={() => setConfirmReportOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MonitorLogoIcon size={16} color="#4f46e5" />
          </Box>
          Conferma generazione Report PDF
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.light',
              bgcolor: 'primary.50'
            }}
          >
            <Typography sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
              Esportazione professionale monitoraggio
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Verrà creato un PDF con dati completi in base ai filtri correnti e reso disponibile nelle notifiche.
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#0f766e" />
              <Typography variant="body2" color="text.secondary">
                Il report userà il tab attivo ({activeTabLabelMap[activeTabForReport] || 'Lista'}).
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#0f766e" />
              <Typography variant="body2" color="text.secondary">
                Includerà la sezione "Elenco Pratiche" ordinata per data assegnazione legale.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#0f766e" />
              <Typography variant="body2" color="text.secondary">
                Includerà la sezione "Dettaglio per Controparte" con raggruppamento pratiche.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AlertTriangle size={16} color="#b45309" />
              <Typography variant="body2" color="text.secondary">
                Il file sarà disponibile in campanella al termine del job.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ mb: 1, fontWeight: 700, color: 'text.primary' }}>
              Filtri correnti inclusi nel PDF
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {reportFiltersPreview.map((item) => {
                const isDefaultValue = ['Tutti', 'Tutte', '-', 'No'].includes(String(item.value));
                return (
                  <Chip
                    key={item.key}
                    size="small"
                    label={`${item.key}: ${item.value}`}
                    color={isDefaultValue ? 'default' : 'primary'}
                    variant={isDefaultValue ? 'outlined' : 'filled'}
                    sx={{ borderRadius: 1.5 }}
                  />
                );
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReportOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmReportOpen(false);
              handleStartMonitoringReportJob();
            }}
          >
            Conferma e genera
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDefaultSortOpen}
        onClose={() => setConfirmDefaultSortOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArrowDownUp size={18} />
          Conferma ordine predefinito
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Verrà ripristinato l'ordinamento standard della lista monitoraggio.
          </Typography>
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#0f766e" />
              <Typography variant="body2" color="text.secondary">
                Colonna `gg` ordinata dal valore più alto al più basso.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#0f766e" />
              <Typography variant="body2" color="text.secondary">
                La lista torna alla prima pagina.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDefaultSortOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmDefaultSortOpen(false);
              handleResetSort();
            }}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MonitorLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Monitoraggio pratiche
              </Typography>
              <Tooltip title="Criteri bucket">
                <IconButton size="small" onClick={() => setIntroOpen(true)} disabled={loading}>
                  <HelpCircle size={18} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Analisi aggiornamento note e task per ogni pratica gestita
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setConfirmDefaultSortOpen(true)}
            disabled={loading || isDefaultSort}
            startIcon={<ArrowDownUp size={16} />}
          >
            Ordine predefinito
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setConfirmReportOpen(true)}
            disabled={loading}
            startIcon={<FileText size={16} />}
          >
            Report PDF
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={users}
            getOptionLabel={(option) => {
              if (!option) return '';
              const name = [option.name, option.surname].filter(Boolean).join(' ');
              return name || `ID ${option.id}`;
            }}
            value={selectedUser}
            onChange={handleSelectUser}
            disabled={!isAdmin}
            disableClearable={!isAdmin}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Referente Legale"
                placeholder=""
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Stato pratica</InputLabel>
            <Select
              value={selectedStatus}
              label="Stato pratica"
              onChange={handleChangeStatus}
            >
              <MenuItem value="">
                <em>Tutti</em>
              </MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Al"
            value={referenceDate}
            onChange={handleChangeReferenceDate}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Decadenza da"
            value={decadenzaFrom}
            onChange={(event) => {
              const value = event.target.value;
              setDecadenzaFrom(value);
              setPage(0);
              fetchMonitoring(selectedUser || null, { page: 0, decadenzaFrom: value });
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Decadenza a"
            value={decadenzaTo}
            onChange={(event) => {
              const value = event.target.value;
              setDecadenzaTo(value);
              setPage(0);
              fetchMonitoring(selectedUser || null, { page: 0, decadenzaTo: value });
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Confronto</InputLabel>
            <Select
              value={comparePeriod}
              label="Confronto"
              onChange={(e) => {
                const value = e.target.value;
                setComparePeriod(value);
                fetchMonitoring(selectedUser || null, { comparePeriod: value });
              }}
            >
              <MenuItem value=""><em>Nessuno</em></MenuItem>
              <MenuItem value="ieri">Ieri</MenuItem>
              <MenuItem value="settimana">7 giorni fa</MenuItem>
              <MenuItem value="mese">30 giorni fa</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            placeholder="Cerca per riferimento, cliente, creditore..."
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              setPage(0);
              fetchMonitoring(selectedUser || null, { search: value, page: 0 });
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Autocomplete
            fullWidth
            size="small"
            options={issuerOptions}
            value={selectedIssuer || null}
            onChange={(event, newValue) => {
              const next = newValue || '';
              setSelectedIssuer(next);
              setPage(0);
              fetchMonitoring(selectedUser || null, { issuer: next, page: 0 });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Controparte"
                placeholder="Cerca controparte..."
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Autocomplete
            fullWidth
            size="small"
            options={categoryOptions}
            value={selectedCategory || null}
            onChange={(event, newValue) => {
              const next = newValue || '';
              setSelectedCategory(next);
              setPage(0);
              fetchMonitoring(selectedUser || null, { category: next, page: 0 });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipologia"
                placeholder="Cerca tipologia..."
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Autocomplete
            fullWidth
            size="small"
            options={urgencyOptions}
            value={selectedUrgency || null}
            onChange={(event, newValue) => {
              const next = newValue || '';
              setSelectedUrgency(next);
              setPage(0);
              fetchMonitoring(selectedUser || null, { urgency: next, page: 0 });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Urgenza"
                placeholder="Cerca urgenza..."
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>% sul totale</InputLabel>
            <Select
              value={incassatoPercentRange}
              label="% sul totale"
              onChange={(event) => {
                const value = event.target.value;
                setIncassatoPercentRange(value);
                setPage(0);
                fetchMonitoring(selectedUser || null, { page: 0, incassatoPercentRange: value });
              }}
            >
              <MenuItem value="">
                <em>Tutte</em>
              </MenuItem>
              <MenuItem value="0">0%</MenuItem>
              <MenuItem value="1-25">1–25%</MenuItem>
              <MenuItem value="26-50">26–50%</MenuItem>
              <MenuItem value="51-75">51–75%</MenuItem>
              <MenuItem value="76-99">76–99%</MenuItem>
              <MenuItem value="100">100%</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={onlyLegalNotes}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setOnlyLegalNotes(checked);
                  setPage(0);
                  fetchMonitoring(selectedUser || null, { onlyLegalNotes: checked, page: 0 });
                }}
              />
            }
            label="Considera solo note referente legale"
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            onClick={() => handleBucketClick('in_regola')}
            sx={{
              cursor: 'pointer',
              border: selectedBucket === 'in_regola' ? '2px solid' : '1px solid transparent',
              borderColor: selectedBucket === 'in_regola' ? 'success.main' : 'transparent',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              In Regola (0–30 gg)
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 0.5 }}>
              {summary.in_regola || 0}
            </Typography>
            <Typography variant="body2" color="success.main">
              Pratiche aggiornate di recente
            </Typography>
            {previousSummary && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {(() => {
                  const curr = summary.in_regola || 0;
                  const prev = previousSummary.in_regola || 0;
                  const delta = curr - prev;
                  const pct = prev > 0 ? ((delta * 100) / prev) : (curr > 0 ? 100 : 0);
                  const label =
                    comparePeriod === 'ieri'
                      ? 'ieri'
                      : comparePeriod === 'settimana'
                      ? '7 giorni fa'
                      : '30 giorni fa';
                  const labeled = previousDateDisplay ? `${label} (${previousDateDisplay})` : label;
                  const deltaLabel = `${delta >= 0 ? '+' : ''}${delta}`;
                  return `Lavorate: ${deltaLabel} (${pct.toFixed(1)}%) rispetto a : ${labeled}`;
                })()}
              </Typography>
            )}
          </StatCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            onClick={() => handleBucketClick('attenzione')}
            sx={{
              cursor: 'pointer',
              border: selectedBucket === 'attenzione' ? '2px solid' : '1px solid transparent',
              borderColor: selectedBucket === 'attenzione' ? 'warning.main' : 'transparent',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Attenzione (31–60 gg)
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 0.5 }}>
              {summary.attenzione || 0}
            </Typography>
            <Typography variant="body2" color="warning.main">
              Pratiche da monitorare
            </Typography>
            {previousSummary && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {(() => {
                  const curr = summary.attenzione || 0;
                  const prev = previousSummary.attenzione || 0;
                  const delta = curr - prev;
                  const pct = prev > 0 ? ((delta * 100) / prev) : (curr > 0 ? 100 : 0);
                  const label =
                    comparePeriod === 'ieri'
                      ? 'ieri'
                      : comparePeriod === 'settimana'
                      ? '7 giorni fa'
                      : '30 giorni fa';
                  const labeled = previousDateDisplay ? `${label} (${previousDateDisplay})` : label;
                  const deltaLabel = `${delta >= 0 ? '+' : ''}${delta}`;
                  return `Lavorate: ${deltaLabel} (${pct.toFixed(1)}%) rispetto a : ${labeled}`;
                })()}
              </Typography>
            )}
          </StatCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            onClick={() => handleBucketClick('critico')}
            sx={{
              cursor: 'pointer',
              border: selectedBucket === 'critico' ? '2px solid' : '1px solid transparent',
              borderColor: selectedBucket === 'critico' ? 'error.main' : 'transparent',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Critico (&gt; 60 gg)
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 0.5 }}>
              {summary.critico || 0}
            </Typography>
            <Typography variant="body2" color="error.main">
              Pratiche urgenti
            </Typography>
            {previousSummary && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {(() => {
                  const curr = summary.critico || 0;
                  const prev = previousSummary.critico || 0;
                  const delta = curr - prev;
                  const pct = prev > 0 ? ((delta * 100) / prev) : (curr > 0 ? 100 : 0);
                  const label =
                    comparePeriod === 'ieri'
                      ? 'ieri'
                      : comparePeriod === 'settimana'
                      ? '7 giorni fa'
                      : '30 giorni fa';
                  const labeled = previousDateDisplay ? `${label} (${previousDateDisplay})` : label;
                  const deltaLabel = `${delta >= 0 ? '+' : ''}${delta}`;
                  return `Lavorate: ${deltaLabel} (${pct.toFixed(1)}%) rispetto a : ${labeled}`;
                })()}
              </Typography>
            )}
          </StatCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            onClick={() => handleBucketClick('senza_note')}
            sx={{
              cursor: 'pointer',
              border: selectedBucket === 'senza_note' ? '2px solid' : '1px solid transparent',
              borderColor: selectedBucket === 'senza_note' ? 'text.primary' : 'transparent',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Senza Note
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 0.5 }}>
              {summary.senza_note || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mai aggiornate o senza data
            </Typography>
            {previousSummary && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {(() => {
                  const curr = summary.senza_note || 0;
                  const prev = previousSummary.senza_note || 0;
                  const delta = curr - prev;
                  const pct = prev > 0 ? ((delta * 100) / prev) : (curr > 0 ? 100 : 0);
                  const label =
                    comparePeriod === 'ieri'
                      ? 'ieri'
                      : comparePeriod === 'settimana'
                      ? '7 giorni fa'
                      : '30 giorni fa';
                  const labeled = previousDateDisplay ? `${label} (${previousDateDisplay})` : label;
                  const deltaLabel = `${delta >= 0 ? '+' : ''}${delta}`;
                  return `Lavorate: ${deltaLabel} (${pct.toFixed(1)}%) rispetto a : ${labeled}`;
                })()}
              </Typography>
            )}
          </StatCard>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, mb: 2 }}>
        <Tabs
          value={viewTab}
          onChange={(event, newValue) => setViewTab(newValue)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab value="list" label="Lista" />
          <Tab value="in_regola" label="In Regola (0–30 gg)" />
          <Tab value="attenzione" label="Attenzione (31–60 gg)" />
          <Tab value="critico" label="Critico (> 60 gg)" />
          <Tab value="senza_note" label="Senza note" />
        </Tabs>
      </Box>

      {viewTab === 'list' ? (
        <StyledCard>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>gg</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Controparte</TableCell>
                  <TableCell>Tipologia</TableCell>
                  <TableCell>Stato Pratica</TableCell>
                  <TableCell>Urgenza</TableCell>
                  <TableCell sortDirection={orderBy === 'data_assegnazione_legale' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'data_assegnazione_legale'}
                      direction={orderBy === 'data_assegnazione_legale' ? order : 'asc'}
                      onClick={() => handleSortColumn('data_assegnazione_legale')}
                    >
                      Data Assegnazione Legale
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Referente Legale</TableCell>
                  <TableCell sortDirection={orderBy === 'data_decadenza' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'data_decadenza'}
                      direction={orderBy === 'data_decadenza' ? order : 'asc'}
                      onClick={() => handleSortColumn('data_decadenza')}
                    >
                      Data Decadenza
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === 'contratti_importo' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'contratti_importo'}
                      direction={orderBy === 'contratti_importo' ? order : 'asc'}
                      onClick={() => handleSortColumn('contratti_importo')}
                    >
                      Contratti
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === 'upsell_importo' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'upsell_importo'}
                      direction={orderBy === 'upsell_importo' ? order : 'asc'}
                      onClick={() => handleSortColumn('upsell_importo')}
                    >
                      Upsell
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === 'incassato_importo' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'incassato_importo'}
                      direction={orderBy === 'incassato_importo' ? order : 'asc'}
                      onClick={() => handleSortColumn('incassato_importo')}
                    >
                      Incassato
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={orderBy === 'incassato_percent' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'incassato_percent'}
                      direction={orderBy === 'incassato_percent' ? order : 'asc'}
                      onClick={() => handleSortColumn('incassato_percent')}
                    >
                      % sul totale
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === 'saldo_da_incassare' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'saldo_da_incassare'}
                      direction={orderBy === 'saldo_da_incassare' ? order : 'asc'}
                      onClick={() => handleSortColumn('saldo_da_incassare')}
                    >
                      Saldo
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={15} align="center" sx={{ py: 3 }}>
                      <LoaderProgress size={50} />
                    </TableCell>
                  </TableRow>
                ) : visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} align="center" sx={{ py: 3 }}>
                      Nessun dato trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{formatDays(item.giorni_da_ultima_nota)}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography sx={{ fontWeight: 'bold' }}>
                            {item.customer_ragione_sociale ||
                              ((item.customer_name || item.customer_surname)
                                ? `${item.customer_name || ''} ${item.customer_surname || ''}`.trim()
                                : item.rif_customer
                                ? `ID: ${item.rif_customer}`
                                : '-')}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            {item.customer_email && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                }}
                              >
                                <Mail size={12} />
                                {item.customer_email}
                              </Box>
                            )}
                            {item.customer_telephone && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                }}
                              >
                                <Phone size={12} />
                                {item.customer_telephone}
                              </Box>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {item.reference}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        {item.issuer_name ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography sx={{ fontWeight: 'bold' }}>
                              {item.issuer_name}
                            </Typography>
                            {item.issuer_pec && (
                              <Typography variant="caption" color="text.secondary">
                                {item.issuer_pec}
                              </Typography>
                            )}
                            {item.issuer_direzione_generale && (
                              <Typography variant="caption" color="text.secondary">
                                {item.issuer_direzione_generale}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{item.category_name || '-'}</TableCell>
                      <TableCell>
                        {item.status ? (
                          <Chip
                            label={item.status}
                            size="small"
                            color={item.status === 'created' || item.status === 'opened' ? 'info' : item.status === 'closed' ? 'success' : 'default'}
                            variant="outlined"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{item.urgency || '-'}</TableCell>
                      <TableCell>
                        {item.data_assegnazione_legale
                          ? new Date(item.data_assegnazione_legale).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {selectedUser && typeof selectedUser.id === 'number' && selectedUser.id > 0
                          ? ([selectedUser.name, selectedUser.surname]
                              .filter(Boolean)
                              .join(' ') || `ID: ${selectedUser.id}`)
                          : selectedUser && selectedUser.id === -1
                          ? 'Senza referente legale'
                          : item.ref_legale || '-'}
                      </TableCell>
                      <TableCell>
                        {item.data_decadenza ? new Date(item.data_decadenza).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(item.contratti_importo)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.upsell_importo)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.incassato_importo)}</TableCell>
                      <TableCell align="center">{renderPercentRing(item.incassato_percent)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 800,
                          color: Number(item.saldo_da_incassare || 0) >= 0 ? 'success.main' : 'error.main',
                        }}
                      >
                        {formatCurrency(item.saldo_da_incassare)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Apri pratica">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/negotiation/${item.id}`)}
                            color="primary"
                          >
                            <ExternalLink size={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Righe per pagina:"
          />
        </StyledCard>
      ) : (
        <StatCard>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Distribuzione per legale
            </Typography>
            <Chip
              size="small"
              label={
                viewTab === 'in_regola'
                  ? 'In Regola (0–30 gg)'
                  : viewTab === 'attenzione'
                  ? 'Attenzione (31–60 gg)'
                  : viewTab === 'critico'
                  ? 'Critico (> 60 gg)'
                  : 'Senza note'
              }
              color={
                viewTab === 'in_regola'
                  ? 'success'
                  : viewTab === 'attenzione'
                  ? 'warning'
                  : viewTab === 'critico'
                  ? 'error'
                  : 'default'
              }
              variant="filled"
            />
          </Box>
          <Box sx={{ height: chartHeightPx }}>
            {legalDistribution.length === 0 ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  fontSize: 14,
                }}
              >
                Nessun dato disponibile per il grafico con i filtri correnti.
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={legalDistributionChartData}
                  layout="vertical"
                  margin={{ top: 10, right: 20, bottom: 10, left: chartLeftMargin }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={yAxisWidth} interval={0} />
                  <RechartsTooltip />
                  <Bar
                    dataKey="value"
                    fill={
                      viewTab === 'in_regola'
                        ? '#22c55e'
                        : viewTab === 'attenzione'
                        ? '#f59e0b'
                        : viewTab === 'critico'
                        ? '#ef4444'
                        : '#6b7280'
                    }
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </StatCard>
      )}
    </Box>
  );
}
